import { useCallback, useEffect, useState } from 'react'
import { tenantUserInvitationListSpec, tenantUserInvitationDeleteSpec, type TenantUserInvitationRow, UserRole, TenantUserRole } from 'aevocam-rest-api-spec'
import { useApi } from '@/hooks/useApi'
import { FilterTextBox } from '@/components/scalar/FilterTextBox'
import { toastError, toastWarning } from '@/components/action/Toaster'
import {
  startLoading,
  doneLoading,
  incrementRefresh,
  type DataGridState,
} from '@/components/dataGrid/createDataGridState'
import { DataGrid, type DataGridColumn, type DataGridRow } from '@/components/dataGrid/DataGrid'
import type { GlobalStateSetter } from '@/utilities/createGlobalState'
import { DataPager } from '@/components/dataGrid/DataPager'
import { toShortDate } from '@/utilities/formatters'
import { IconButton } from '@/components/action/IconButton'
import { TenantInvitationAdd } from './TenantInvitationAdd'

//TODO: Don't allow duplicate email or name in invitations for same tenant

type Props = {
  tenantId: string
}

export function TenantInvitations({ tenantId }: Props) {
  const api = useApi({ requireLogin: true })
  const userRole = api.status.session?.userRole
  const tenantUserRole = api.status.session?.tenantUserRole
  const canEdit =
    userRole === UserRole.Admin ||
    tenantUserRole === TenantUserRole.Owner ||
    tenantUserRole === TenantUserRole.Admin

  //--------------------------------------------------------------------------------
  const [invitationListState, setInvitationListState] = useState<TenantUserInvitationListState>({
    filterText: '',
    rows: [],
    totalRows: 0,
    skip: 0,
    take: 10,
  })

  //--------------------------------------------------------------------------------
  const setInvitationsFilterText = useCallback((filterText: string) => {
    setInvitationListState(prev => ({ ...prev, filterText, skip: 0 }))
  }, [])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    async function fn() {
      startLoading(setInvitationListState as GlobalStateSetter<DataGridState<TenantUserInvitationRow>>, invitationListState.refreshQuietly)
      try {
        const refreshIncrement = invitationListState.refresh ?? 0
        const res = await api.request(tenantUserInvitationListSpec, {
          path: { tenant: tenantId },
          body: {
            filterText: invitationListState.filterText || undefined,
            skip: invitationListState.skip,
            take: invitationListState.take,
          },
        })
        if (res.status === 200 && res.data?.invitations) {
          setInvitationListState(prev => {
            if (prev.refresh !== refreshIncrement) return prev
            return {
              ...prev,
              rows: res.data?.invitations?.rows ?? [],
              totalRows: res.data?.invitations?.totalRows ?? 0,
            }
          })
        }
      } catch {
        toastError('Failed to load invitations')
      } finally {
        doneLoading(setInvitationListState as GlobalStateSetter<DataGridState<TenantUserInvitationRow>>)
      }
    }
    void fn()
  }, [tenantId, api, invitationListState.skip, invitationListState.take, invitationListState.refresh, invitationListState.filterText])

  //--------------------------------------------------------------------------------
  const handleRefreshInvitations = useCallback(() => {
    incrementRefresh(setInvitationListState)
  }, [])

  const handleDeleteInvitation = useCallback(async (row: TenantUserInvitationRow) => {
    if (row.id == null) return
    if (!confirm('Delete this invitation?')) return
    const res = await api.request(tenantUserInvitationDeleteSpec, {
      path: { tenant: tenantId, invitation: String(row.id) },
    })
    if (res.status === 200) {
      toastWarning('Invitation deleted')
      incrementRefresh(setInvitationListState)
    } else {
      toastError(res.error ?? 'Failed to delete invitation')
    }
  }, [tenantId, api, setInvitationListState, toastError, toastWarning])

  //--------------------------------------------------------------------------------
  return (
    <div className='Cutout Wrapped'>
      <h2>Membership invitations</h2>

      <div className='FlexRow AlignCenter'>
        <div className='FlexGrow'>
          <FilterTextBox
            value={invitationListState.filterText ?? ''}
            setValue={setInvitationsFilterText}
          />
        </div>
        <DataPager<TenantUserInvitationListState>
          state={invitationListState}
          setState={setInvitationListState}
          alignRight
        />
      </div>

      <DataGrid
        state={invitationListState}
        setState={setInvitationListState}
        idColumn='id'
        columns={tenantUserInvitationColumns(canEdit, handleDeleteInvitation)}
      />

      {canEdit && (
        <TenantInvitationAdd
          tenantId={tenantId}
          onAdded={handleRefreshInvitations}
        />
      )}

    </div>
  )
}


//--------------------------------------------------------------------------------
type TenantUserInvitationListState = DataGridState<TenantUserInvitationRow> & {
  filterText?: string
}

//--------------------------------------------------------------------------------
function tenantUserInvitationColumns(
  canEdit: boolean,
  onDeleteInvitation: (row: TenantUserInvitationRow) => void | Promise<void>,
): DataGridColumn<TenantUserInvitationRow>[] {
  return [
    {
      id: 'id',
      label: '',
      className: () => 'NoWrap',
      render: (_value, { row }: DataGridRow<TenantUserInvitationRow>) => (
        <IconButton
          icon='times'
          tooltip='Delete invitation'
          disabled={!canEdit}
          onClick={() => void onDeleteInvitation(row)}
        />
      ),
    },
    {
      id: 'email',
      label: 'Email',
      render: (value: string | undefined) => value || '-',
    },
    {
      id: 'name',
      label: 'Name',
      render: (value: string | undefined) => value || '-',
    },
    {
      id: 'message',
      label: 'Message',
      render: (value: string | undefined) => value || '-',
    },
    {
      id: 'role',
      label: 'Role',
      render: (value: TenantUserRole | undefined) =>
        value != null ? TenantUserRole[value] : '',
    },
    {
      id: 'expiresAt',
      label: 'Expires',
      render: (value: string | undefined) => (value ? toShortDate(value) : ''),
    },
  ]
}
